/**
 * A dispute history record represents a snapshot of dispute information at a specific point in time.
 *
 * Records are automatically created whenever a dispute changes status, stage, or other key properties, providing a complete audit trail for compliance and audit requirements.
 */
export interface DisputeHistoryRecord {
  /**
   * Dispute history record ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  id?: string;
  /**
   * Revision number, which increments by 1 each time the dispute history record is updated.
   * To prevent conflicting changes, the existing revision must be specified when updating a dispute history record.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the dispute history record was created.
   * @readonly
   * @immutable
   */
  createdDate?: Date | null;
  /**
   * Date and time the dispute history record was last updated.
   * @readonly
   * @immutable
   */
  updatedDate?: Date | null;
  /**
   * ID of the dispute that this history record is a snapshot of.
   * @format GUID
   * @immutable
   */
  disputeId?: string;
  /**
   * Stage of the dispute process.
   * @readonly
   * @immutable
   */
  stage?: DisputeStageWithLiterals;
  /**
   * Status of the dispute at the time this record was created.
   * Indicates what action was required and by whom at this point in the dispute lifecycle.
   * @readonly
   * @immutable
   */
  status?: DisputeStatusWithLiterals;
  /**
   * The latest date and time until which the dispute can remain in its current status.
   *
   * If this date passes, the dispute will be resolved against the party that is required to take action. For example, if the status is `WAITING_MERCHANT`, and the due date passes, the dispute will be `LOST` and a refund will be processed automatically.
   * @readonly
   * @immutable
   */
  dueDate?: Date | null;
  /**
   * ID of the charge that is being disputed.
   * @format GUID
   * @readonly
   * @immutable
   */
  chargeId?: string;
  /**
   * Channel through which the dispute is being processed.
   * @readonly
   * @immutable
   */
  channel?: DisputeChannelWithLiterals;
  /**
   * Custom field data for the dispute history record object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Tags assigned to the dispute history record for organization and filtering purposes. */
  tags?: Tags;
}

export enum DisputeStage {
  /** Formal dispute filed through the card network or issuing bank. */
  CHARGEBACK = 'CHARGEBACK',
  /** Initial inquiry from the customer requesting information about the charge. */
  INQUIRY = 'INQUIRY',
}

/** @enumType */
export type DisputeStageWithLiterals = DisputeStage | 'CHARGEBACK' | 'INQUIRY';

export enum DisputeStatus {
  /** Waiting for merchant action, such as submitting evidence or accepting the dispute. */
  WAITING_MERCHANT = 'WAITING_MERCHANT',
  /** Dispute is under review by the payment service provider (internal channel) or bank (external channel). */
  UNDER_REVIEW = 'UNDER_REVIEW',
  /** Waiting for buyer action or response. */
  WAITING_BUYER = 'WAITING_BUYER',
  /** Dispute was resolved in favor of the merchant. */
  WON = 'WON',
  /** Dispute was resolved in favor of the cardholder. */
  LOST = 'LOST',
}

/** @enumType */
export type DisputeStatusWithLiterals =
  | DisputeStatus
  | 'WAITING_MERCHANT'
  | 'UNDER_REVIEW'
  | 'WAITING_BUYER'
  | 'WON'
  | 'LOST';

export enum DisputeChannel {
  /** Dispute is processed by the payment service provider. */
  INTERNAL = 'INTERNAL',
  /** Dispute is processed through the card network or issuing bank. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type DisputeChannelWithLiterals =
  | DisputeChannel
  | 'INTERNAL'
  | 'EXTERNAL';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

/** Triggered when tags are modified on a dispute history record. */
export interface TagsModified {
  /** Updated dispute history record. */
  disputeHistoryRecord?: DisputeHistoryRecord;
  /** Tags that were assigned to the dispute history record. */
  assignedTags?: Tags;
  /** Tags that were unassigned from the dispute history record. */
  unassignedTags?: Tags;
}

export interface GetDisputeHistoryRecordRequest {
  /**
   * ID of the dispute history record to retrieve.
   * @format GUID
   */
  disputeHistoryRecordId: string;
}

export interface GetDisputeHistoryRecordResponse {
  /** The retrieved dispute history record. */
  disputeHistoryRecord?: DisputeHistoryRecord;
}

export interface QueryDisputeHistoryRecordsRequest {
  /** Wix Query Language expression for filtering, sorting, and paging dispute history records. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDisputeHistoryRecordsResponse {
  /** Retrieved dispute history records. */
  disputeHistoryRecords?: DisputeHistoryRecord[];
  /** Paging metadata for cursor-based pagination. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface BulkUpdateDisputeHistoryRecordTagsRequest {
  /**
   * List of dispute history records IDs to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /** Tags to assign to the dispute history records. */
  assignTags?: Tags;
  /** Tags to unassign from the dispute history records. */
  unassignTags?: Tags;
}

export interface BulkUpdateDisputeHistoryRecordTagsResponse {
  /**
   * Results of the bulk tag update operation for each dispute history record.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateDisputeHistoryRecordTagsResult[];
  /** Metadata about the bulk update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateDisputeHistoryRecordTagsResult {
  /** Metadata about the individual item update operation. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateDisputeHistoryRecordTagsByFilterRequest {
  /**
   * Filter that determines which disputes to update tags for.
   *
   * An empty filter updates all disputes.
   */
  filter: Record<string, any> | null;
  /** Tags to assign to the filtered dispute history records. */
  assignTags?: Tags;
  /** Tags to remove from the filtered dispute history records. */
  unassignTags?: Tags;
}

export interface BulkUpdateDisputeHistoryRecordTagsByFilterResponse {
  /**
   * Job ID for tracking the asynchronous bulk update operation.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateDisputeHistoryRecordTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateDisputeHistoryRecordTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
